import { AdminExtWriteback, DefaultApiGetWritebacksRequest } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  AdminTableFooter,
  LoaderWithPerfTimings,
  Typography,
  FlexRow,
  GoogleSheet,
  Excel,
  FlexColumn,
  SortDropdown,
  Tooltip,
  DataSourceInfoWrap,
  ImportTypeIcon,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { KEY_LABEL } from "../../../constants";
import { getScheduleSummaryText, getTimeZone, useApiContext } from "../../../utils";
import { TabName } from "../../WorkspaceAdmin/WorkspaceAdmin";
import { useTableFilters } from "../useTableFilters";

import { getSortIcon } from "./DataImports";
import { IconDropdown } from "./IconDropdown";
import { WritebackDetailsDrawer } from "./WritebackDetailsDrawer";
import { WritebacksFilterToolbar } from "./WritebacksFilterToolbar";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

export const Writebacks: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const history = useHistory();

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [activeWritebackId, setActiveWritebackId] = React.useState<string>();

  const [hoveredDataSourceId, setHoveredDataSourceId] = useState<string>();

  const { data: hoveredDataSourceResponse } = useQuery(
    [QueryCacheKey.ADMIN_EXT_DATA_SOURCE, hoveredDataSourceId],
    () => adminExtApiClient.getDataSource({ data_source_id: hoveredDataSourceId! }),
    { enabled: Boolean(hoveredDataSourceId) }
  );

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "last_run_dt",
    value: SortDirection.descend,
  });

  const [page, setPage] = React.useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = useState(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetWritebacksRequest>([
    "data_source_id",
    "data_source_type",
    "user_id",
    "status",
    "trigger",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const { data: writebacksResponse, isLoading } = useQuery(
    [
      QueryCacheKey.ADMIN_EXT_DATA_WRITEBACKS,
      domain,
      pageSize,
      pageOffset,
      sortedColumn,
      filters,
      search,
    ],
    () =>
      adminExtApiClient.getWritebacks({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search,
        data_source_id: filters.data_source_id as unknown as string,
        data_source_type: filters.data_source_type as unknown as string,
        user_id: filters.user_id as unknown as string,
        status: filters.status as unknown as string,
        trigger: filters.trigger as unknown as string,
      })
  );

  const writebacks = writebacksResponse?.data.writebacks ?? [];

  const columns: ColumnsType<AdminExtWriteback> = [
    {
      title: <Typography noWrap>Export</Typography>,
      dataIndex: "writeback_name",
      key: "writeback_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="writeback_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "writeback_name"),
      render: (writebackName: AdminExtWriteback["writeback_name"], writeback) => {
        return (
          <FlexRow style={{ maxWidth: 300 }}>
            {writeback.google_sheets_file_name ? (
              <GoogleSheet height="24px" width="24px" />
            ) : (
              <Excel height="24px" width="24px" />
            )}

            <FlexColumn gap={0} minWidthZero>
              <Typography fontWeight={500} title={writebackName} noWrap>
                {writebackName}
              </Typography>

              <Typography
                fontSize="13px"
                color="textSecondary"
                title={writeback.google_sheets_file_name ?? writeback.excel_sheets_file_name}
                noWrap
                underline={Boolean(writeback.spreadsheet_url)}
                onClick={e => {
                  e.stopPropagation();

                  writeback.spreadsheet_url
                    ? window.open(writeback.spreadsheet_url, "_blank")
                    : null;
                }}
              >
                {writeback.google_sheets_file_name ?? writeback.excel_sheets_file_name ?? "-"}
              </Typography>
            </FlexColumn>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>Destination</Typography>,
      dataIndex: "data_source_name",
      key: "data_source_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_source_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_source_name"),
      render: (dataSourceName: AdminExtWriteback["data_source_name"], writeback) => {
        return (
          <div
            onMouseEnter={() => setHoveredDataSourceId(writeback.data_source_id)}
            onMouseLeave={() => setHoveredDataSourceId(undefined)}
          >
            <Tooltip
              variant="light"
              placement="topLeft"
              title={
                hoveredDataSourceId && hoveredDataSourceResponse?.data.data_source ? (
                  <DataSourceInfoWrap dataSource={hoveredDataSourceResponse.data.data_source} />
                ) : undefined
              }
            >
              <FlexRow>
                <ImportTypeIcon size="small" type={writeback.data_source_type} />

                <Typography noWrap>{dataSourceName}</Typography>

                {writeback.object_type_name ? (
                  <span
                    style={{
                      backgroundColor: "#CDEEF3",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {writeback.object_type_name}
                  </span>
                ) : null}
              </FlexRow>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Action</Typography>,
      dataIndex: "writeback_mode",
      key: "writeback_mode",
      showSorterTooltip: false,
      width: 100,
      render: (mode: string) => mode ?? "-",
    },
    {
      title: <Typography noWrap>Field Mapping</Typography>,
      dataIndex: "field_mappings",
      key: "field_mappings",
      showSorterTooltip: false,
      width: 160,
      render: (_: unknown, writeback) => {
        const mappings = writeback.field_mappings;
        if (!mappings || mappings.length === 0) {
          return "-";
        }

        return (
          <Tooltip
            variant="light"
            placement="bottomLeft"
            overlayStyle={{ maxWidth: "none" }}
            overlayInnerStyle={{ padding: "12px 16px" }}
            title={
              <table style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "4px 12px",
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#888",
                        borderBottom: "1px solid #e8e8e8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Sheet Column
                    </th>
                    <th style={{ borderBottom: "1px solid #e8e8e8", padding: "4px 12px" }} />
                    <th
                      style={{
                        textAlign: "left",
                        padding: "4px 0",
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#888",
                        borderBottom: "1px solid #e8e8e8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Destination Field
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <tr key={i}>
                      <td style={{ padding: "4px 12px", whiteSpace: "nowrap" }}>
                        {m.ss_column_name}
                      </td>
                      <td style={{ padding: "4px 12px", color: "#999" }}>→</td>
                      <td style={{ padding: "4px 0", whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            backgroundColor: "#F5F5F5",
                            borderRadius: 4,
                            padding: "2px 8px",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {m.source_field_name}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <span
              style={{
                borderBottom: "1px dashed currentColor",
                cursor: "default",
              }}
            >
              {mappings.length} mapped field{mappings.length !== 1 ? "s" : ""}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: <Typography noWrap>Last Run</Typography>,
      dataIndex: "last_run_status_type",
      key: "last_run_status_type",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="last_run_dt" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "last_run_dt"),
      render: (status: AdminExtWriteback["last_run_status_type"], writeback) => {
        return status ? (
          <StatusTag
            // Keep in sync with spec.WritebackRunOverallStatusType (coeff/server/spec.py)
            status={
              status === "ok" ? "ok" : status === "some_records_failed" ? "warning" : "failed"
            }
            label={
              writeback.last_run_dt ? (
                <Typography
                  inline
                  title={moment(writeback.last_run_dt).format("MMM D, YYYY h:mma")}
                >
                  {moment(writeback.last_run_dt).fromNow()}
                </Typography>
              ) : undefined
            }
          />
        ) : (
          "-"
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.created_by}</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_by" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_by"),
      render: (createdBy: AdminExtWriteback["created_by"], writeback) => (
        <div style={{ maxWidth: 200 }}>
          <Typography title={writeback.created_by_email} noWrap>
            {createdBy}
          </Typography>

          {writeback.created_dt ? (
            <>
              <Typography
                title={moment(writeback.created_dt).format("MMM D, YYYY h:mma")}
                noWrap
                color="textSecondary"
                fontSize="13px"
              >
                {moment(writeback.created_dt).fromNow()}
              </Typography>
            </>
          ) : null}
        </div>
      ),
    },
    {
      title: <Typography noWrap>Schedule</Typography>,
      dataIndex: "is_scheduled",
      key: "is_scheduled",
      showSorterTooltip: false,
      width: 200,
      render: (isScheduled: boolean, writeback) =>
        isScheduled ? (
          <Typography noWrap>
            {getScheduleSummaryText(writeback.schedule_info, null, { prefixText: "Exports " })}
          </Typography>
        ) : (
          <Typography color="textSecondary">None</Typography>
        ),
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (writeback: AdminExtWriteback) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconDropdown
            dropdownProps={{
              overlay: (
                <Menu>
                  <Menu.Item
                    onClick={() => {
                      const params = new URLSearchParams(location.search);
                      params.append("writeback_id", JSON.stringify([writeback.writeback_id]));
                      history.push({
                        pathname: location.pathname
                          .split("/")
                          .slice(0, -1)
                          .concat(TabName.writeback_runs)
                          .join("/"),
                        search: params.toString(),
                      });
                    }}
                  >
                    View Run History
                  </Menu.Item>

                  <Menu.Item
                    onClick={() => {
                      window.open(writeback.spreadsheet_url, "_blank");
                    }}
                  >
                    Open Sheet
                  </Menu.Item>
                </Menu>
              ),
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Container>
      <WritebacksFilterToolbar
        domain={domain}
        initialSearch={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          onRow={writeback => ({
            onClick: () => {
              setActiveWritebackId(writeback.writeback_id);
            },
          })}
          columns={columns}
          rowKey={r => r.writeback_id}
          dataSource={writebacks.map(d => ({ ...d, key: d.writeback_id })) ?? []}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="Writebacks.locale" />
            ) : (
              "No exports found"
            ),
          }}
          pagination={false}
        />

        <AdminTableFooter
          page={page}
          pageSize={pageSize}
          onPageChange={page => setPageAndOffset(page)}
          data={writebacks.map(x => [x.writeback_id, true])}
          totalRowCount={writebacksResponse?.data.total_records ?? 0}
        />

        <WritebackDetailsDrawer
          writebackId={activeWritebackId}
          onClose={() => setActiveWritebackId(undefined)}
        />
      </FlexColumn>
    </Container>
  );
};
